import { useToast } from "@src/appV2/lib";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { isNaN } from "lodash";
import { type ChangeEvent, type KeyboardEvent, useState } from "react";

import { formatToSSN } from "../utils/formatToSSN";

export function useAddSsn() {
  const { showErrorToast } = useToast();
  const worker = useDefinedWorker();
  const [ssn, setSSN] = useState<string>(
    worker?.socialSecurityNumber ? `xxx-xx-${worker.socialSecurityNumber}` : ""
  );

  function handleChangeSSN(event: ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;

    const formattedValue = value?.toString().replace(/[^\dx-]/g, "");

    setSSN(formattedValue);
  }

  function handleKeyUpSSN(event: KeyboardEvent<HTMLInputElement>) {
    // Use currentTarget instead of target to get proper TypeScript typing.
    // - target: the element that triggered the event (typed as EventTarget, no 'value' property)
    // - currentTarget: the element the handler is attached to (typed as HTMLInputElement, has 'value')
    // For input fields, both refer to the same element, so no behavioral change from agentSSN.tsx
    // which used event.target["value"] with bracket notation to bypass type checking.
    const { value } = event.currentTarget;

    if (event.key !== "Backspace" && event.key !== "Delete") {
      setSSN(formatToSSN(value));
      return;
    }

    setSSN(value);
  }

  function handleKeyPressSSN(event: KeyboardEvent<HTMLInputElement>) {
    if (isNaN(Number(event.key))) {
      event.preventDefault();
    }
  }

  async function handleSaveSSN(onSave: (ssnNumber: string) => Promise<void>) {
    if (!/^(?!666|000|9\d{2})\d{3}-(?!00)\d{2}-(?!0{4})\d{4}$/.test(ssn)) {
      showErrorToast("SSN is invalid");
      return;
    }

    const ssnNumber = ssn.replace(/\D/g, "");

    await onSave(ssnNumber);
  }

  const isDisabled = ssn.length !== 11;

  return {
    ssn,
    handleChangeSSN,
    handleKeyUpSSN,
    handleKeyPressSSN,
    handleSaveSSN,
    isDisabled,
  };
}
